import { Link } from 'react-router-dom';

export const TermsContent = () => (
  <>
    <p>
      <strong>Welcome to Spreadsheet Arena, offered by MERIDIAN!</strong>
    </p>

    <p>
      These Spreadsheet Arena Terms of Service (these &ldquo;Terms&rdquo;)
      describe the terms and conditions by which you may access and/or use the
      Spreadsheet Arena website(s), including www.spreadsheetarena.ai, and any
      and all related software, documentation, and online, mobile-enabled,
      and/or digital services (collectively, the &ldquo;Service&rdquo;) provided
      by Longitude Labs, Inc. (including its successors and assigns,
      &ldquo;Meridian,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or
      &ldquo;us&rdquo;). By accessing and/or using the Service, you&rsquo;re
      agreeing to these Terms and acknowledging that you have read and
      understood our <Link to="/privacy">Privacy Notice</Link>. If you
      don&rsquo;t agree to these Terms, you may not use the Service. We reserve
      the right to modify these Terms, as described below. These Terms apply to
      all visitors and users of the Service, and to all others who access the
      Service (collectively, &ldquo;Users,&rdquo; and, as applicable to you,
      &ldquo;you&rdquo; or &ldquo;your&rdquo;).
    </p>

    <p>
      PLEASE READ THESE TERMS CAREFULLY TO ENSURE THAT YOU UNDERSTAND EACH
      PROVISION. THESE TERMS CONTAIN A MANDATORY INDIVIDUAL ARBITRATION
      PROVISION IN SECTION 14.2 (THE &ldquo;ARBITRATION AGREEMENT&rdquo;) AND A
      CLASS ACTION/JURY TRIAL WAIVER PROVISION IN SECTION 14.3 (THE &ldquo;CLASS
      ACTION/JURY TRIAL WAIVER&rdquo;) THAT REQUIRE, UNLESS YOU OPT OUT PURSUANT
      TO THE INSTRUCTIONS IN THE ARBITRATION AGREEMENT, THE EXCLUSIVE USE OF
      FINAL AND BINDING ARBITRATION ON AN INDIVIDUAL BASIS TO RESOLVE DISPUTES
      BETWEEN YOU AND US, INCLUDING ANY CLAIMS THAT AROSE OR WERE ASSERTED
      BEFORE YOU AGREED TO THESE TERMS. TO THE FULLEST EXTENT PERMITTED BY
      APPLICABLE LAW (AS DEFINED BELOW), YOU EXPRESSLY WAIVE YOUR RIGHT TO SEEK
      RELIEF IN A COURT OF LAW AND TO HAVE A JURY TRIAL ON YOUR CLAIMS, AS WELL
      AS YOUR RIGHT TO PARTICIPATE AS A PLAINTIFF OR CLASS MEMBER IN ANY CLASS,
      COLLECTIVE, PRIVATE ATTORNEY GENERAL, OR REPRESENTATIVE ACTION OR
      PROCEEDING.
    </p>

    <h2>1. How We Administer the Service</h2>

    <p>
      <strong>1.1 Service Description.</strong> The Service enables you to
      compare the Output (as defined below) of certain AI Services (as defined
      below) and vote for such AI Services based on your preference.
    </p>

    <p>
      <strong>1.2 Eligibility.</strong> This is a contract between you and
      Meridian. You must read and agree to these Terms before using the Service.
      You may use the Service only if you can form a legally binding contract
      with us, and only in compliance with these Terms and all applicable local,
      state, national, and international laws, rules, and regulations
      (&ldquo;Applicable Law&rdquo;). To use the Service, you must be at least
      13 years old. You represent and warrant that you meet the applicable age
      requirements and are competent to agree to these Terms, or, if you are
      unable to form a binding contract under applicable law, you represent and
      warrant that you have your parent&rsquo;s or legal guardian&rsquo;s
      permission to use the Service, and that your parent or legal guardian is
      agreeing to these Terms concurrently. The Service is not available to any
      Users we previously removed from the Service.
    </p>

    <h3>1.3 User Accounts</h3>

    <p>
      <strong>(a) Your User Account; Suspension and Termination.</strong> Your
      account on the Service (your &ldquo;User Account&rdquo;) gives you access
      to certain services and functionalities that we may, in our sole
      discretion, establish and maintain as part of the Service from time to
      time. We may, with or without prior notice, permanently terminate or
      temporarily suspend your access to your User Account and/or the Service
      without liability and for any or no reason, including if you violate any
      provision of these Terms. Additionally, you may de-activate your User
      Account at any time. We may, with or without prior notice, change or stop
      providing the Service, to you or to Users generally, or create usage
      limits for the Service.
    </p>

    <p>
      <strong>(b) Connecting Via Third-Party Services.</strong> By connecting to
      the Service via a third-party service, you give us permission to access
      and use your information from that service.
    </p>

    <p>
      <strong>(c) Account Security.</strong> You may never use another
      User&rsquo;s User Account without such User&rsquo;s permission. You are
      solely responsible for the activity that occurs on your User Account, you
      will keep your User Account password(s) and/or any other authentication
      credentials secure, and you will not share your password(s) and/or any
      other authentication credentials with anyone else. We encourage you to use
      &ldquo;strong&rdquo; passwords (passwords that use a combination of upper-
      and lower-case letters, numbers, and symbols) to protect your User
      Account. Any Org User with administrator-level access to your User Account
      can modify your User Account settings, access, and billing information. We
      will not be liable for, and expressly disclaim liability for, any losses
      caused by any unauthorized use of your User Account and/or any changes to
      your User Account. You will notify us immediately of any breach of
      security or unauthorized use of your User Account.
    </p>

    <p>
      <strong>(d) Account Settings.</strong> You may control certain aspects of
      your User Account and any associated User profile, and of the way you
      interact with the Service, by changing the settings in your settings page.
      By providing us with your email address, you consent to our using that
      email address to send you Service-related notices. If we send you
      marketing-related emails, you may opt out of receiving them or change your
      preferences by contacting the Service support team at
      contact@spreadsheetarena.ai or by clicking on the
      &ldquo;unsubscribe&rdquo; link within a marketing email. Opting out will
      not prevent you from receiving Service-related notices.
    </p>

    <p>
      <strong>1.4 Your Interactions with Other Users.</strong> YOU ARE SOLELY
      RESPONSIBLE FOR YOUR INTERACTIONS, INCLUDING SHARING OF INFORMATION, WITH
      OTHER USERS. WE RESERVE THE RIGHT TO MONITOR DISPUTES BETWEEN YOU AND
      OTHER USERS. WE EXPRESSLY DISCLAIM ALL LIABILITY ARISING FROM YOUR
      INTERACTIONS WITH OTHER USERS, AND FOR ANY USER&rsquo;S ACTION OR
      INACTION, INCLUDING RELATING TO USER CONTENT (AS DEFINED BELOW).
    </p>

    <h2>2. Access to the Service; Service Restrictions</h2>

    <p>
      <strong>2.1 Access to the Service.</strong> Subject to your compliance
      with these Terms and any documentation we may make available to you, you
      are hereby granted a non-exclusive, limited, non-transferable, and freely
      revocable right to access and use the Service, solely for your personal
      use or internal business purposes, as permitted by the features of the
      Service. We reserve all rights not expressly granted herein in and to the
      Service.
    </p>

    <p>
      <strong>2.2 Restrictions and Acceptable Use.</strong> Except to the extent
      a restriction is prohibited by Applicable Law, you will not do, and will
      not enable any third party to do, any of the following:
    </p>

    <ol type="a">
      <li>
        disassemble, reverse engineer, decode, or decompile any part of the
        Service or license or resell or modify any part of the Service;
      </li>
      <li>
        use any automated or non-automated means to access the Service for
        &ldquo;scraping&rdquo; (except that public search engines may use
        spiders to create searchable indices of public materials, only as
        specified in the robots.txt file);
      </li>
      <li>
        use the Service in any manner that impacts the stability of the servers
        or the operation or performance of the Service or any User&rsquo;s use
        of the Service;
      </li>
      <li>
        copy, rent, lease, sell, loan, transfer, assign, sublicense, resell,
        distribute, modify, alter, or create derivative works of any part of the
        Service or any of our intellectual property;
      </li>
      <li>
        use the Service in any manner that (i) violates any Applicable Law,
        contractual obligation, or right of any person, (ii) is fraudulent,
        false, deceptive, or defamatory, (iii) promotes hatred, violence, or
        harm against, or (iv) otherwise may be harmful or objectionable to us or
        any other third party;
      </li>
      <li>
        use the Service in competition with us, to develop competing products or
        services, for benchmarking or competitive analysis of the Service, or
        otherwise to our detriment or disadvantage;
      </li>
      <li>
        bypass the measures we may use to prevent or restrict access to the
        Service;
      </li>
      <li>
        use the Service to transmit spam or other unsolicited email (and we may
        immediately remove any content that we believe to be spam) or use the
        Service for commercial solicitation;
      </li>
      <li>
        access any content available on or via the Service through any
        technology or means other than those provided by the Service or
        authorized by us;
      </li>
      <li>
        attempt to interfere with, compromise the system integrity or security
        of, or decipher any transmissions to or from, the servers running the
        Service;
      </li>
      <li>
        transmit invalid data, viruses, worms, or other software agents through
        the Service;
      </li>
      <li>collect or harvest any personal information from the Service; or</li>
      <li>
        refer to us or to the Service in a manner that could imply a
        relationship that involves endorsement, affiliation, or sponsorship
        between you (or a third party) and us without our consent.
      </li>
    </ol>

    <h2>3. Generated Content and User Content</h2>

    <p>
      <strong>3.1</strong> You may be allowed to submit text, documents, images
      and other materials to the Service for processing (&ldquo;Input&rdquo;) by
      certain third-party artificial intelligence and deep learning platforms,
      algorithms, services, tools, and models (&ldquo;AI Services&rdquo;), and
      receive output based on such materials (&ldquo;Output&rdquo;). Due to the
      nature of machine learning, use of the Service may result in incorrect
      Output. You must evaluate the accuracy of any Output as appropriate for
      your use case, including by using human review of the Output. You agree
      that we shall not be liable for any damages you or any third party alleges
      to incur as a result of or relating to any Output or other content
      generated by or accessed on or through the Service. You acknowledge that
      we make available the Output of AI Services for comparison purposes only
      and we are not the provider of any AI Service, nor are we responsible for
      any such AI Service or any Output.
    </p>

    <p>
      <strong>3.2</strong> As between us and you, you (or your licensors) will
      own any and all information, data, Input and other content that is
      collected or otherwise received by us from you through the Service
      (&ldquo;User Content&rdquo;).
    </p>

    <p>
      <strong>3.3</strong> We claim no ownership rights over User Content, and,
      as between you and us, all User Content that is submitted, posted,
      displayed, provided, shared, or otherwise made available on or via the
      Service by you is and will remain yours. You understand that certain
      portions of the Service may allow other Users to view, edit, share, and/or
      otherwise interact with your User Content and your Output (as defined
      below), and you agree to allow others to view, edit, share, and/or
      interact with your User Content and Output in accordance with your
      settings and these Terms. We have the right (but not the obligation) in
      our sole discretion to (i) identify you as the contributor of your User
      Content in accordance with your settings and these Terms in any
      publication in any form, media or technology now known or later developed
      in connection with your User Content, and (ii) remove any of your User
      Content that is shared via the Service. You hereby grant each User a
      non-exclusive license to access your User Content and Output through the
      Service, and to use, reproduce, distribute, display and perform your User
      Content and Output, which you make available to such User through the
      Service. You further grant, and you represent and warrant that you have
      all rights necessary to grant, to us, under all of your intellectual
      property rights, a non-exclusive and royalty-free right and license to
      use, copy, store, modify, distribute, reproduce, publish, list, make
      derivative works of, and display your User Content and Output: (i) to
      maintain and provide the Service; (ii) to improve our products and the
      Service and for our other business purposes (including, but not limited
      to, labeling and classification purposes); and (iii) to perform such other
      actions as described in our <Link to="/privacy">Privacy Notice</Link> or
      as authorized by you in connection with your use of the Service.
    </p>

    <p>
      <strong>3.4</strong> You affirm, represent, and warrant the following: (a)
      you have obtained, and are solely responsible for obtaining, all consents
      required by Applicable Law to provide User Content relating to third
      parties; (b) your User Content and Output and our use thereof as
      contemplated by these Terms and the Service will not violate any
      Applicable Law or infringe any rights of any third party, including, but
      not limited to, any intellectual property rights, privacy rights and
      confidentiality rights; (c) you will not upload or make available through
      the Service, either directly or by other means, any personal information
      of children under 13 or the applicable age of digital consent; and (d)
      your User Content does not include sexually suggestive content; hate
      speech or direct attacks on an individual or group; content that is
      abusive, harassing, defamatory, vulgar, libelous, or invasive of
      another&rsquo;s privacy; sexist or racially, ethnically, or otherwise
      discriminatory content; content that contains self-harm or excessive
      violence; impostor profiles; content in furtherance of harmful or illegal
      activities; malicious programs or code; any person&rsquo;s personal
      information without such person&rsquo;s consent; spam messages; and/or
      otherwise objectionable content.
    </p>

    <p>
      <strong>3.5</strong> We take no responsibility and assume no liability for
      any User Content. You will be solely responsible for your User Content and
      the consequences of submitting, posting, displaying, providing, sharing,
      or otherwise making it available on or through the Service, and you
      understand and acknowledge that we are acting only as a passive conduit
      for your online distribution and publication of your User Content. WE TAKE
      NO RESPONSIBILITY AND ASSUME NO LIABILITY FOR ANY USER CONTENT. YOU WILL
      BE SOLELY RESPONSIBLE FOR YOUR USER CONTENT AND THE CONSEQUENCES OF
      SUBMITTING, POSTING, DISPLAYING, PROVIDING, SHARING, OR OTHERWISE MAKING
      IT AVAILABLE ON OR THROUGH THE SERVICE, AND YOU UNDERSTAND AND ACKNOWLEDGE
      THAT WE ARE ACTING ONLY AS A PASSIVE CONDUIT FOR YOUR ONLINE DISTRIBUTION
      AND PUBLICATION OF YOUR USER CONTENT. YOU UNDERSTAND AND ACKNOWLEDGE THAT
      THE SERVICE MAY EXPOSE YOU TO CONTENT THAT IS INACCURATE, OBJECTIONABLE,
      INAPPROPRIATE FOR CHILDREN, OR OTHERWISE UNSUITED TO YOUR PURPOSE, AND YOU
      UNDERSTAND AND ACKNOWLEDGE THAT WE WILL NOT BE LIABLE FOR ANY DAMAGES YOU
      ALLEGE TO INCUR AS A RESULT OF OR RELATING TO ANY CONTENT ACCESSED ON OR
      THROUGH THE SERVICE.
    </p>

    <h3>3.6 Your Use of Third-Party Services</h3>

    <p>
      THE SERVICE MAY CONTAIN LINKS TO THIRD-PARTY SITES, MATERIALS, AND/OR
      SERVICES (COLLECTIVELY, &ldquo;THIRD-PARTY SERVICES&rdquo;) THAT ARE NOT
      OWNED OR CONTROLLED BY US, AND CERTAIN FUNCTIONALITIES OF THE SERVICE MAY
      REQUIRE YOUR USE OF THIRD-PARTY SERVICES, INCLUDING CERTAIN THIRD-PARTY AI
      SERVICES, TO WHICH YOU ARE SUBJECT TO AND AGREE TO THE THIRD PARTY&rsquo;S
      TERMS AND CONDITIONS MADE AVAILABLE VIA ITS SERVICES. WE HEREBY DISCLAIM
      ALL LIABILITY RESULTING FROM OR RELATING TO THE OPERATION, MAINTENANCE,
      FUNCTION, FAILURE, OR SECURITY OF ANY THIRD-PARTY SERVICE, THE ACTS OR
      OMISSIONS OF ANY PROVIDER OF A THIRD-PARTY SERVICE, ANY OUTPUT GENERATED
      BY A THIRD-PARTY SERVICE, AND ANY DECISION, ACT, OR OMISSION TAKEN BY YOU
      AS A RESULT OF ANY OF THE FOREGOING. WE DO NOT ENDORSE OR ASSUME ANY
      RESPONSIBILITY FOR ANY THIRD-PARTY SERVICES. IF YOU ACCESS A THIRD-PARTY
      SERVICE FROM THE SERVICE OR SHARE YOUR USER CONTENT OR OUTPUT ON OR
      THROUGH ANY THIRD-PARTY SERVICE, YOU DO SO AT YOUR OWN RISK, AND YOU
      UNDERSTAND THAT THESE TERMS AND OUR PRIVACY NOTICE DO NOT APPLY TO YOUR
      USE OF ANY THIRD-PARTY SERVICE. YOU EXPRESSLY RELIEVE US FROM ANY AND ALL
      LIABILITY ARISING FROM YOUR ACCESS TO AND/OR USE OF ANY THIRD-PARTY
      SERVICE. ADDITIONALLY, YOUR DEALINGS WITH, OR PARTICIPATION IN PROMOTIONS
      OF, ADVERTISERS FOUND ON THE SERVICE ARE SOLELY BETWEEN YOU AND SUCH
      ADVERTISERS. YOU UNDERSTAND AND ACKNOWLEDGE THAT WE WILL NOT BE
      RESPONSIBLE FOR ANY LOSS OR DAMAGE OF ANY SORT RELATING TO YOUR DEALINGS
      WITH SUCH ADVERTISERS.
    </p>

    <h2>4. Intellectual Property</h2>

    <p>
      <strong>4.1 Meridian Intellectual Property.</strong> You understand and
      acknowledge that we (or our licensors (including other Users), as
      applicable) own and will continue to own all rights (including
      intellectual property rights), title, and interest in and to the Service,
      all materials and content displayed or otherwise made available on and/or
      through the Service (excluding your User Content), and all software,
      algorithms, code, technology, and intellectual property underlying and/or
      included in or with the Service. Use of any intellectual property for any
      purpose not expressly permitted by these Terms is strictly prohibited.
    </p>

    <p>
      <strong>4.2 Usage Data.</strong> We may collect, or you may provide to us,
      diagnostic, technical, usage, and/or related information, including
      information about your computers, mobile devices, systems, and software
      (collectively, &ldquo;Usage Data&rdquo;). All Usage Data is and will be
      owned solely and exclusively by us, and, to the extent any ownership
      rights in or to the Usage Data vest in you, you hereby assign to us all
      rights (including intellectual property rights), title, and interest in
      and to same. Accordingly, we may use, maintain, and/or process the Usage
      Data or any portion thereof for any lawful purpose, including, without
      limitation: (a) to provide and maintain the Service; (b) to improve our
      products and services (including the Service), and to develop new
      products, services, and/or features; (c) to monitor your usage of the
      Service; (d) for research and analytics, including, without limitation,
      data analysis, identifying usage trends, and/or customer research; and (e)
      to share analytics and other derived Usage Data with third parties, solely
      in de-identified or aggregated form. The Service may contain technological
      measures designed to prevent unauthorized or illegal use of the Service;
      you understand and acknowledge that we may use these and other lawful
      measures to verify your compliance with these Terms and to enforce our
      rights, including intellectual property rights, in and to the Service.
    </p>

    <p>
      <strong>4.3 Open Source Software.</strong> Some software used in our
      Service may be offered under an open source license that we make available
      to you. There may be provisions in an open source license that expressly
      override some of these terms, so please be sure to read those licenses.
    </p>

    <p>
      <strong>4.4 Feedback.</strong> To the extent you provide us any
      suggestions, recommendations, or other feedback relating to the Service or
      to any other Meridian products or services (collectively,
      &ldquo;Feedback&rdquo;), you hereby assign to us all rights (including
      intellectual property rights), title, and interest in and to the Feedback,
      without providing any attribution or compensation to you or to any third
      party. Please treat Feedback as our Confidential Information (as defined
      below).
    </p>

    <p>
      <strong>4.5 Meridian Property.</strong> The Service contains data,
      information, and/or other content, such as reputational or status
      indicators, in-world currency, and/or fictional property representing
      virtual achievements (for instance, badges, trophies, and/or powers)
      (collectively, &ldquo;Meridian Property&rdquo;). Notwithstanding any
      terminology that may suggest you own or have any claims to any Meridian
      Property, we solely and exclusively own all Meridian Property and we
      reserve all rights to same, except that we hereby grant you a
      non-exclusive, limited, non-transferable, and freely revocable license to
      use certain Meridian Property as permitted by the Service&rsquo;s
      functionalities. Without limiting the generality of the foregoing, (a)
      Meridian Property is not redeemable for any sum of money or monetary value
      from Meridian at any time, and (b) we have the absolute right to manage,
      regulate, control, modify, transfer, and/or delete Meridian Property as we
      see fit, in our sole discretion, in any general or specific case, and we
      will have no liability to you based on our exercise of such right.
      NOTWITHSTANDING ANY VALUE ATTRIBUTED TO SUCH DATA BY YOU OR BY ANY THIRD
      PARTY, ANY DATA, USER ACCOUNT HISTORY, AND USER ACCOUNT CONTENT RESIDING
      ON THE SERVERS RUNNING THE SERVICE MAY BE ALTERED, TRANSFERRED, OR DELETED
      AT ANY TIME, FOR ANY REASON OR NO REASON, IN OUR SOLE DISCRETION, WITH OR
      WITHOUT NOTICE AND WITH NO LIABILITY OF ANY KIND. WE DO NOT PROVIDE OR
      GUARANTEE, AND WE EXPRESSLY DISCLAIM, ANY VALUE, CASH OR OTHERWISE,
      ATTRIBUTED TO ANY DATA RESIDING ON THE SERVERS RUNNING THE SERVICE.
    </p>

    <h2>5. Confidential Information</h2>

    <p>
      The Service may include non-public, proprietary, or confidential
      information of Meridian and/or of other Users (&ldquo;Confidential
      Information&rdquo;). Confidential Information includes any information
      that should reasonably be understood to be confidential given the nature
      of the information and the circumstances of disclosure, including
      non-public business, product, technology, and marketing information. You
      will: (a) protect and safeguard the confidentiality of all Confidential
      Information with at least the same degree of care as you would use protect
      your own highly sensitive confidential information, but in no event with
      less than a reasonable degree of care; (b) not use any Confidential
      Information for any purpose other than to exercise your rights, or to
      perform your obligations, under these Terms; and (c) not disclose any
      Confidential Information to any person or entity, except your service
      providers or financial or legal advisors who/that (i) need to know the
      Confidential Information and (ii) are bound by non-use and non-disclosure
      restrictions at least as restrictive as those set forth in this Section.
    </p>

    <h2>6. DMCA Notice</h2>

    <p>
      We respect artists and content owner rights, and it is our policy to
      respond to alleged infringement notices that comply with the Digital
      Millennium Copyright Act of 1998 (as it may be amended,
      &ldquo;DMCA&rdquo;).
    </p>

    <p>
      If you believe that your copyrighted work has been copied in a way that
      constitutes copyright infringement and is accessible via the Service,
      please notify our copyright agent as set forth in the DMCA. For your
      complaint to be valid under the DMCA, you must provide all of the
      following information in writing:
    </p>

    <ol type="a">
      <li>
        a signature of a person authorized to act on behalf of the copyright
        owner;
      </li>
      <li>
        identification of the copyrighted work that you claim has been
        infringed;
      </li>
      <li>
        identification of the material that is claimed to be infringing and its
        location on the Service;
      </li>
      <li>
        information reasonably sufficient to permit us to contact you, such as
        your address, telephone number, and email address;
      </li>
      <li>
        a statement that you have a good faith belief that use of the material
        in the manner complained of is not authorized by the copyright owner,
        its agent, or law; and
      </li>
      <li>
        a statement, made under penalty of perjury, that the above information
        is accurate, and that you are the copyright owner or are authorized to
        act on behalf of the owner.
      </li>
    </ol>

    <p>
      The above information must be submitted to our DMCA Agent, using the
      following contact information:
    </p>

    <p>
      Attn: DMCA Notice
      <br />
      Longitude Labs, Inc.
      <br />
      Email: <a href="mailto:legal@meridian.ai">legal@meridian.ai</a>
    </p>

    <p>
      UNDER UNITED STATES FEDERAL LAW, IF YOU KNOWINGLY MISREPRESENT THAT ONLINE
      MATERIAL IS INFRINGING, YOU MAY BE SUBJECT TO CRIMINAL PROSECUTION FOR
      PERJURY AND CIVIL PENALTIES, INCLUDING MONETARY DAMAGES, COURT COSTS, AND
      ATTORNEYS&rsquo; FEES.
    </p>

    <p>
      Please note that the procedure outlined herein is exclusively for
      notifying Meridian and its affiliates that your copyrighted material has
      been infringed. The preceding requirements are intended to comply with
      Meridian&rsquo;s rights and obligations under the DMCA, including 17
      U.S.C. &sect;512(c), but do not constitute legal advice. It may be
      advisable to contact an attorney regarding your rights and obligations
      under the DMCA and other Applicable Law.
    </p>

    <p>
      In accordance with the DMCA and other Applicable Law, we have adopted a
      policy of terminating, in appropriate circumstances, Users who are deemed
      to be repeat infringers. We may also, at our sole discretion, limit access
      to the Service and/or terminate the User Accounts of any Users who
      infringe any intellectual property rights of others, whether or not there
      is any repeat infringement.
    </p>

    <h2>7. Our Publicity Rights</h2>

    <p>
      We may identify you as a User in our promotional materials. We will
      promptly stop doing so upon receipt of your request sent to{' '}
      <a href="mailto:legal@meridian.ai">legal@meridian.ai</a>.
    </p>

    <h2>8. Fees and Payment</h2>

    <p>
      <strong>8.1 General Payment Terms.</strong> We currently offer the Service
      free of charge. However, we retain the right to charge fees
      (&ldquo;Fees&rdquo;) for certain services or any features or components
      thereof (&ldquo;Paid Services&rdquo;).
    </p>

    <p>
      <strong>8.2 Your Payment Method.</strong> To use Paid Services, you must
      provide us with at least one (1) valid payment card that is accepted by us
      and Payment Processor (each such card, a &ldquo;Payment Method&rdquo;). We
      use a third-party payment processor (the &ldquo;Payment Processor&rdquo;)
      to bill you through the Payment Method linked to your User Account for any
      owed Fees. The processing of payments will be subject to the terms,
      conditions and privacy policies of the Payment Processor in addition to
      this Agreement. We are not responsible for any error by, or other acts or
      omissions of, the Payment Processor. By choosing to use Paid Services, you
      agree to pay us, through the Payment Processor, all Fees then in effect
      for any use of such Paid Services with the applicable payment terms, and
      you authorize us, through the Payment Processor, to charge your Payment
      Method. You agree to make payment using that selected Payment Method. We
      reserve the right to correct any errors or mistakes that the Payment
      Processor makes even if it has already requested or received payment.
    </p>

    <p>
      <strong>8.3 Taxes.</strong> Except as may be expressly stated in these
      Terms, all Fees must be paid in advance, payment obligations are
      non-cancelable once incurred (subject to any cancellation rights set forth
      in these Terms), and Fees paid are non-refundable. Fees are stated
      exclusive of any taxes, levies or duties (collectively, but, for clarity,
      excluding taxes based on our net income, &ldquo;Taxes&rdquo;). You will be
      responsible for paying all Taxes associated with your purchases and/or
      Paid Services Subscriptions in connection with the Service.
    </p>

    <p>
      <strong>8.4 Payment Representations and Warranties.</strong> If you used
      Paid Services, you represent and warrant that: (i) the account, order, and
      Payment Method information you supply to us and/or to Payment Processor,
      as applicable, is true, accurate, correct, and complete; (ii) you are duly
      authorized to use the Payment Method(s); (iii) you will pay any and all
      charges incurred by users of your Payment Method in connection with the
      Service, including any applicable Fees (at the prices in effect when such
      charges are incurred) and Taxes; (iv) charges incurred by you will be
      honored by your Payment Method company; (v) you will not allow or enable
      anyone else to use your Subscription (including, without limitation, by
      sharing your password(s) or any other authentication credentials with
      anyone else, or by attempting to transfer your Subscription to anyone
      else); and (vi) you will report to us any unauthorized or prohibited
      access to or use of your Subscription and/or password(s) or other
      authentication credentials.
    </p>

    <h2>9. Privacy; Data Security</h2>

    <p>
      <strong>9.1 Privacy.</strong> We care about your privacy. To provide and
      enhance the Service, we may need to be able to identify you and your
      interests, and we use your personal data to do this. By using the Service,
      you acknowledge that we may collect, use, and disclose your personal
      information and aggregated and/or anonymized data as set forth in our{' '}
      <Link to="/privacy">Privacy Notice</Link>, and that your personal
      information may be transferred to, and/or processed in, the United States.
    </p>

    <p>
      <strong>9.2 Security.</strong> We care about the integrity and security of
      your personal information. However, we cannot guarantee that unauthorized
      third parties will never be able to defeat our security measures or to use
      your data for improper purposes. You acknowledge that you provide your
      data at your own risk.
    </p>

    <h2>10. Release</h2>

    <p>
      You hereby release us from all claims, damages (whether direct, indirect,
      incidental, consequential, or otherwise), obligations, losses,
      liabilities, costs, debts, and expenses, in each case of every kind and
      nature, known and unknown, arising out of a dispute between you and a
      third party (including any other User) in connection with the Service. In
      addition, you waive any Applicable Law that says, in substance: &ldquo;A
      GENERAL RELEASE DOES NOT EXTEND TO CLAIMS WHICH THE RELEASING PARTY DOES
      NOT KNOW OR SUSPECT TO EXIST IN HIS OR HER FAVOR AT THE TIME OF EXECUTING
      THE RELEASE, WHICH, IF KNOWN BY HIM OR HER, WOULD HAVE MATERIALLY AFFECTED
      HIS OR HER SETTLEMENT WITH THE RELEASED PARTY.&rdquo;
    </p>

    <h2>11. Indemnity</h2>

    <p>
      You will defend, indemnify, and hold us and our subsidiaries and
      affiliates, and our and their respective agents, suppliers, licensors,
      employees, contractors, officers, and directors (collectively, including
      Meridian, the &ldquo;Meridian Indemnitees&rdquo;) harmless from and
      against any and all claims, damages (whether direct, indirect, incidental,
      consequential, or otherwise), obligations, losses, liabilities, costs,
      debts, and expenses (including, but not limited to, legal fees) arising
      from: (a) your access to and/or use of the Service, including your use of
      Output; (b) your violation of any term of these Terms; (c) your violation
      of any third-party right, including, without limitation, any privacy right
      or intellectual property right; (d) your violation of any Applicable Law;
      (e) User Content or any content that is submitted via your User Account;
      (f) your willful misconduct; or (g) any third party&rsquo;s access to
      and/or use of the Service with your authentication credential(s).
    </p>

    <h2>12. No Warranty; Disclaimers</h2>

    <p>
      THE SERVICE IS PROVIDED ON AN &ldquo;AS IS&rdquo; AND &ldquo;AS
      AVAILABLE&rdquo; BASIS. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW,
      THE SERVICE, THE INTELLECTUAL PROPERTY, AND ANY OTHER INFORMATION
      AVAILABLE ON OR THROUGH THE SERVICE ARE PROVIDED WITHOUT WARRANTIES OF ANY
      KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED
      WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND/OR
      NON-INFRINGEMENT. NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN,
      OBTAINED BY YOU FROM US OR THROUGH THE SERVICE WILL CREATE ANY WARRANTY
      NOT EXPRESSLY STATED HEREIN.
    </p>

    <p>
      WITHOUT LIMITING THE GENERALITY OF THE FOREGOING, NONE OF THE MERIDIAN
      INDEMNITEES WARRANTS THAT ANY CONTENT OR ANY OTHER INFORMATION CONTAINED
      IN, OR AVAILABLE VIA, THE SERVICE IS ACCURATE, COMPREHENSIVE, RELIABLE,
      USEFUL, OR CORRECT; THAT THE SERVICE WILL MEET YOUR REQUIREMENTS; THAT THE
      SERVICE WILL BE AVAILABLE AT ANY PARTICULAR TIME OR LOCATION,
      UNINTERRUPTED, OR SECURE; THAT ANY DEFECTS OR ERRORS IN THE SERVICE WILL
      BE CORRECTED; OR THAT THE SERVICE IS FREE OF VIRUSES OR OTHER HARMFUL
      COMPONENTS. ANY CONTENT DOWNLOADED OR OTHERWISE OBTAINED THROUGH THE USE
      OF THE SERVICE IS SO OBTAINED AT YOUR OWN RISK, AND YOU WILL BE SOLELY
      RESPONSIBLE FOR ANY DAMAGE TO YOUR COMPUTER SYSTEM(S) OR MOBILE DEVICE(S)
      AND/OR FOR LOSS OF DATA THAT RESULTS FROM SAME OR FROM YOUR ACCESS TO
      AND/OR USE OF THE SERVICE. YOU MAY HAVE OTHER STATUTORY RIGHTS, BUT THE
      DURATION OF STATUTORILY REQUIRED WARRANTIES, IF ANY, WILL BE LIMITED TO
      THE SHORTEST PERIOD PERMITTED BY APPLICABLE LAW.
    </p>

    <p>
      FURTHER, MERIDIAN DOES NOT WARRANT, ENDORSE, GUARANTEE, RECOMMEND, OR
      ASSUME RESPONSIBILITY FOR ANY PRODUCT OR SERVICE ADVERTISED OR OFFERED BY
      ANY THIRD PARTY THROUGH THE SERVICE OR ANY HYPERLINKED WEBSITE OR SERVICE,
      AND MERIDIAN WILL NOT BE A PARTY TO, OR IN ANY WAY MONITOR, ANY
      TRANSACTION BETWEEN YOU AND THIRD-PARTY PROVIDERS OF PRODUCTS OR SERVICES.
    </p>

    <h2>13. Limitation of Liability</h2>

    <p>
      TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL ANY
      MERIDIAN INDEMNITEE BE LIABLE FOR ANY INDIRECT, PUNITIVE, INCIDENTAL,
      SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING, WITHOUT
      LIMITATION, DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, OR DATA, OR OTHER
      INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO THE USE OF, OR INABILITY
      TO USE, THE SERVICE OR ANY PORTION THEREOF. UNDER NO CIRCUMSTANCES WILL WE
      BE RESPONSIBLE FOR ANY DAMAGE, LOSS, OR INJURY RESULTING FROM HACKING,
      TAMPERING, OR OTHER UNAUTHORIZED ACCESS TO OR USE OF THE SERVICE OR YOUR
      USER ACCOUNT OR THE INFORMATION CONTAINED THEREIN. IN NO EVENT WILL ANY
      MERIDIAN INDEMNITEE BE LIABLE TO YOU FOR ANY CLAIMS, PROCEEDINGS,
      LIABILITIES, OBLIGATIONS, DAMAGES, LOSSES, OR COSTS IN AN AMOUNT EXCEEDING
      THE AMOUNT YOU PAID TO US HEREUNDER OR ONE HUNDRED U.S. DOLLARS ($100.00),
      WHICHEVER IS GREATER. THIS LIMITATION OF LIABILITY SECTION APPLIES WHETHER
      THE ALLEGED LIABILITY IS BASED ON CONTRACT, TORT, NEGLIGENCE, STRICT
      LIABILITY, OR ANY OTHER BASIS, EVEN IF WE HAVE BEEN ADVISED OF THE
      POSSIBILITY OF SUCH DAMAGE.
    </p>

    <p>
      TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, WE ASSUME NO LIABILITY
      OR RESPONSIBILITY FOR ANY (A) ERRORS, MISTAKES, OR INACCURACIES OF
      CONTENT; (B) PERSONAL INJURY OR PROPERTY DAMAGE, OF ANY NATURE WHATSOEVER,
      RESULTING FROM YOUR ACCESS TO OR USE OF THE SERVICE; (C) ANY UNAUTHORIZED
      ACCESS TO OR USE OF THE SERVERS RUNNING THE SERVICE AND/OR ANY AND ALL
      PERSONAL INFORMATION STORED THEREIN; (D) ANY INTERRUPTION OR CESSATION OF
      TRANSMISSION TO OR FROM THE SERVICE; (E) ANY BUGS, VIRUSES, TROJAN HORSES,
      OR THE LIKE THAT MAY BE TRANSMITTED TO OR THROUGH THE SERVICE BY ANY THIRD
      PARTY; (F) ANY ERRORS OR OMISSIONS IN ANY CONTENT, OR ANY LOSS OR DAMAGE
      INCURRED AS A RESULT OF THE USE OF ANY CONTENT POSTED, EMAILED,
      TRANSMITTED, OR OTHERWISE MADE AVAILABLE THROUGH THE SERVICE; AND/OR (G)
      YOUR DATA, ANY USER CONTENT, OR THE DEFAMATORY, OFFENSIVE, OR ILLEGAL
      CONDUCT OF ANY THIRD PARTY.
    </p>

    <p>
      THE DISCLAIMERS, EXCLUSIONS, AND LIMITATIONS OF LIABILITY UNDER THESE
      TERMS WILL NOT APPLY TO THE EXTENT PROHIBITED BY APPLICABLE LAW.
    </p>

    <h2>14. Governing Law, Arbitration, and Class Action/Jury Trial Waiver</h2>

    <p>
      <strong>14.1 Governing Law.</strong> These Terms will be governed by the
      laws of the State of New York, without respect to its conflict of laws
      principles. Notwithstanding the preceding sentences with respect to the
      substantive law governing these Terms, the Federal Arbitration Act (9
      U.S.C. &sect;&sect; 1-16) (as it may be amended, &ldquo;FAA&rdquo;)
      governs the interpretation and enforcement of the Arbitration Agreement
      below and preempts all state laws (and laws of other jurisdictions) to the
      fullest extent permitted by Applicable Law. If the FAA is found to not
      apply to any issue that arises from or relates to the Arbitration
      Agreement, then that issue will be resolved under and governed by the law
      of the U.S. state where you live (if applicable) or the jurisdiction
      mutually agreed upon in writing by you and us. The application of the
      United Nations Convention on Contracts for the International Sale of Goods
      is expressly excluded. You agree to submit to the exclusive personal
      jurisdiction of the federal and state courts located in New York for any
      actions for which we retain the right to seek injunctive or other
      equitable relief in a court of competent jurisdiction to prevent the
      actual or threatened infringement, misappropriation, or violation of our
      data security, Confidential Information, or intellectual property rights,
      as set forth in the Arbitration Agreement below, including any provisional
      relief required to prevent irreparable harm. You agree that New York is
      the proper and exclusive forum for any appeals of an arbitration award, or
      for trial court proceedings in the event that the Arbitration Agreement
      below is found to be unenforceable. These Terms were drafted in the
      English language and this English language version of the Terms is the
      original, governing instrument of the understanding between you and us. In
      the event of any conflict between the English version of these Terms and
      any translation, the English version will prevail.
    </p>

    <h3>14.2 Arbitration Agreement</h3>

    <p>
      <strong>(a) General.</strong> READ THIS SECTION CAREFULLY BECAUSE IT
      REQUIRES THE PARTIES TO ARBITRATE THEIR DISPUTES AND LIMITS THE MANNER IN
      WHICH YOU CAN SEEK RELIEF FROM US. This Arbitration Agreement applies to
      and governs any dispute, controversy, or claim between you and us that
      arises out of or relates to, directly or indirectly: (i) these Terms; (ii)
      access to or use of the Service, including receipt of any advertising or
      marketing communications; (iii) any transactions through, by, or using the
      Service; or (iv) any other aspect of your relationship or transactions
      with us as a User or consumer (each, a &ldquo;Claim,&rdquo; and,
      collectively, &ldquo;Claims&rdquo;). This Arbitration Agreement will
      apply, without limitation, to all Claims that arose or were asserted
      before or after your consent to these Terms.
    </p>

    <p>
      <strong>(b) Opting Out of Arbitration Agreement.</strong> If you are a new
      User, you can reject and opt out of this Arbitration Agreement within
      thirty (30) days of accepting these Terms by emailing us at
      <a href="mailto:legal@meridian.ai">legal@meridian.ai</a> with your full,
      legal name and stating your intent to opt out of this Arbitration
      Agreement. Opting out of this Arbitration Agreement does not affect the
      binding nature of any other part of these Terms, including the provisions
      regarding controlling law or the courts in which any disputes must be
      brought.
    </p>

    <p>
      <strong>(c) Dispute-Resolution Process.</strong> For any Claim, you will
      first contact us at{' '}
      <a href="mailto:legal@meridian.ai">legal@meridian.ai</a> and attempt to
      resolve the Claim with us informally. In the unlikely event that we have
      not been able to resolve a Claim after sixty (60) days, we each agree to
      resolve such Claim exclusively through binding arbitration by JAMS before
      a single arbitrator (the &ldquo;Arbitrator&rdquo;), under the Optional
      Expedited Arbitration Procedures then in effect for JAMS (the
      &ldquo;Rules&rdquo;), except as provided herein. JAMS may be contacted at
      www.jamsadr.com, where the Rules are available. In the event of any
      conflict between the Rules and this Arbitration Agreement, this
      Arbitration Agreement will control. The arbitration will be conducted in
      the U.S. county where you live (if applicable) or New York County, New
      York, unless you and Meridian agree otherwise. If you are using the
      Service for commercial purposes, each party will be responsible for paying
      any JAMS filing and administrative fees and Arbitrator fees in accordance
      with the Rules, and the award rendered by the Arbitrator will include
      costs of arbitration, reasonable attorneys&rsquo; fees, and reasonable
      costs for expert and other witnesses. If you are an individual using the
      Service for non-commercial purposes: (i) JAMS may require you to pay a fee
      for the initiation of your case, unless you apply for and successfully
      obtain a fee waiver from JAMS; (ii) the award rendered by the Arbitrator
      may include your costs of arbitration, your reasonable attorneys&rsquo;
      fees, and your reasonable costs for expert and other witnesses; and (iii)
      you may sue in a small claims court of competent jurisdiction without
      first engaging in arbitration, but this would not absolve you of your
      commitment to engage in the informal dispute resolution process. Any
      judgment on the award rendered by the Arbitrator may be entered in any
      court of competent jurisdiction. You and we agree that the Arbitrator, and
      not any federal, state, or local court or agency, will have exclusive
      authority to resolve any disputes relating to the scope, interpretation,
      applicability, enforceability, or formation of this Arbitration Agreement,
      including any claim that all or any part of this Arbitration Agreement is
      void or voidable. The Arbitrator will also be responsible for determining
      all threshold arbitrability issues, including issues relating to whether
      these Terms are, or whether any provision of these Terms is,
      unconscionable or illusory, and any defense to arbitration, including
      waiver, delay, laches, unconscionability, and/or estoppel.
    </p>

    <p>
      <strong>(d) Equitable Relief.</strong> NOTHING IN THIS ARBITRATION
      AGREEMENT WILL BE DEEMED AS: PREVENTING US FROM SEEKING INJUNCTIVE OR
      OTHER EQUITABLE RELIEF FROM THE COURTS AS NECESSARY TO PREVENT THE ACTUAL
      OR THREATENED INFRINGEMENT, MISAPPROPRIATION, OR VIOLATION OF OUR DATA
      SECURITY, CONFIDENTIAL INFORMATION, OR INTELLECTUAL PROPERTY RIGHTS; OR
      PREVENTING YOU FROM ASSERTING CLAIMS IN A SMALL CLAIMS COURT, PROVIDED
      THAT YOUR CLAIMS QUALIFY AND SO LONG AS THE MATTER REMAINS IN SUCH COURT
      AND ADVANCES ON ONLY AN INDIVIDUAL (NON-CLASS, NON-COLLECTIVE, AND
      NON-REPRESENTATIVE) BASIS.
    </p>

    <p>
      <strong>(e) Severability.</strong> If this Arbitration Agreement is found
      to be void, unenforceable, or unlawful, in whole or in part, the void,
      unenforceable, or unlawful provision, in whole or in part, will be
      severed. Severance of the void, unenforceable, or unlawful provision, in
      whole or in part, will have no impact on the remaining provisions of this
      Arbitration Agreement, which will remain in force, or on the
      parties&rsquo; ability to compel arbitration of any remaining Claims on an
      individual basis pursuant to this Arbitration Agreement. Notwithstanding
      the foregoing, if the Class Action/Jury Trial Waiver below is found to be
      void, unenforceable, or unlawful, in whole or in part, because it would
      prevent you from seeking public injunctive relief, then any dispute
      regarding the entitlement to such relief (and only that relief) must be
      severed from arbitration and may be litigated in a civil court of
      competent jurisdiction. All other claims for relief subject to arbitration
      under this Arbitration Agreement will be arbitrated under its terms, and
      the parties agree that litigation of any dispute regarding the entitlement
      to public injunctive relief will be stayed pending the outcome of any
      individual claims in arbitration.
    </p>

    <p>
      <strong>14.3 Class Action/Jury Trial Waiver.</strong> BY ENTERING INTO
      THESE TERMS, YOU AND MERIDIAN ARE EACH WAIVING THE RIGHT TO A TRIAL BY
      JURY OR TO BRING, JOIN, OR PARTICIPATE IN ANY PURPORTED CLASS ACTION,
      COLLECTIVE ACTION, PRIVATE ATTORNEY GENERAL ACTION, OR OTHER
      REPRESENTATIVE PROCEEDING OF ANY KIND AS A PLAINTIFF OR CLASS MEMBER. THE
      FOREGOING APPLIES TO ALL USERS (BOTH NATURAL PERSONS AND ENTITIES),
      REGARDLESS OF WHETHER YOU HAVE OBTAINED OR USED THE SERVICE FOR PERSONAL,
      COMMERCIAL, OR OTHER PURPOSES. THIS CLASS ACTION/JURY TRIAL WAIVER APPLIES
      TO CLASS ARBITRATION, AND, UNLESS WE AGREE OTHERWISE, THE ARBITRATOR MAY
      NOT CONSOLIDATE MORE THAN ONE PERSON&rsquo;S OR ENTITY&rsquo;S CLAIMS. YOU
      AND MERIDIAN AGREE THAT THE ARBITRATOR MAY AWARD RELIEF ONLY TO AN
      INDIVIDUAL CLAIMANT AND ONLY TO THE EXTENT NECESSARY TO PROVIDE RELIEF ON
      YOUR INDIVIDUAL CLAIM(S). ANY RELIEF AWARDED MAY NOT AFFECT OTHER USERS.
    </p>

    <h2>15. U.S. Government Restricted Rights</h2>

    <p>
      To the extent the Service is being used by or on behalf of the U.S.
      Government, the Service will be deemed commercial computer software or
      commercial computer software documentation (as applicable). Accordingly,
      if you are an agency of the U.S. Government or any contractor therefor,
      you receive only those rights with respect to the Service as are granted
      to all other Users hereunder, in accordance with 48 C.F.R. &sect;227.7202
      and 48 C.F.R. &sect;12.212, as applicable.
    </p>

    <h2>16. Export Controls</h2>

    <p>
      You understand and acknowledge that the Service may be subject to export
      control laws and regulations. You will comply with all applicable import
      and export and re-export control and trade and economic sanctions laws and
      regulations, including the Export Administration Regulations maintained by
      the U.S. Department of Commerce, trade and economic sanctions maintained
      by the U.S. Treasury Department&rsquo;s Office of Foreign Assets Control
      (&ldquo;OFAC&rdquo;), and the International Traffic in Arms Regulations
      maintained by the U.S. State Department. You represent and warrant that
      you are not, and that no person to whom you make the Service available or
      that is acting on your behalf, or, if you are an Organization, that no
      person or entity owning 50% or more of your equity securities or other
      equivalent voting interests, is (a) listed on the List of Specially
      Designated Nationals and Blocked Persons or on any other list of
      sanctioned, prohibited, or restricted parties administered by OFAC or by
      any other governmental entity, or (b) located in, a national or resident
      of, or a segment of the government of, any country or territory for which
      the United States maintains trade or economic sanctions or embargoes or
      that has been designated by the U.S. Government as a &ldquo;terrorist
      supporting&rdquo; region.
    </p>

    <h2>17. General Provisions</h2>

    <p>
      <strong>17.1 Assignment.</strong> These Terms, and any rights and licenses
      granted hereunder, may not be transferred or assigned by you without our
      prior express written consent, but may be assigned by us without
      restriction. Any attempted transfer or assignment in violation hereof will
      be null and void.
    </p>

    <p>
      <strong>17.2 Notification Procedures and Changes to these Terms.</strong>{' '}
      We may provide notifications to you via email notice or through posting of
      such notice on the Service, as we determine in our sole discretion. We may
      modify or update these Terms from time to time, and you should review this
      page periodically. These Terms apply to and govern your access to and use
      of the Service effective as of the start of your access to the Service,
      even if such access began before publication of these Terms. Your
      continued use of the Service after any change to these Terms constitutes
      your acceptance of the new Terms of Service. If you do not agree to any
      part of these Terms or to any future Terms of Service, do not access or
      use (or continue to access or use) the Service.
    </p>

    <p>
      <strong>17.3 Entire Agreement; Severability.</strong> These Terms,
      together with any amendments and any additional agreements you may enter
      into with us in connection with the Service, will constitute the entire
      agreement between you and us concerning the Service. Except as otherwise
      stated in the Arbitration Agreement, if any provision of these Terms is
      deemed invalid by a court of competent jurisdiction, the invalidity of
      such provision will not affect the validity of the remaining provisions of
      these Terms, which will remain in full force and effect.
    </p>

    <p>
      <strong>17.4 No Waiver.</strong> No waiver of any term of these Terms will
      be deemed a further or continuing waiver of such term or of any other
      term, and our failure to assert any right or provision under these Terms
      will not constitute a waiver of such right or provision.
    </p>

    <p>
      <strong>17.5 California Residents.</strong> The provider of the Service is
      set forth herein. If you are a California resident, in accordance with
      Cal. Civ. Code &sect;1789.3, you may report complaints to the Complaint
      Assistance Unit of the Division of Consumer Services of the California
      Department of Consumer Affairs by contacting it in writing at 1625 North
      Market Blvd., Suite N 112 Sacramento, CA 95834, or by telephone at (800)
      952-5210 or (916) 445-1254.
    </p>

    <p>
      <strong>17.6 Contact.</strong> If you have any questions about these Terms
      and/or the Service, please contact us at{' '}
      <a href="mailto:legal@meridian.ai">legal@meridian.ai</a>.
    </p>
  </>
);
